/**
 * The public records, as reactive state.
 *
 * `GET /api/records` needs no session and works for a guest exactly like
 * everyone else — a global "who holds the record" figure is not a property
 * of who is asking, so this store has no `guest`/`signed-in` distinction of
 * its own, the same reason `daily.svelte.ts`'s `DailyStatus` does not.
 *
 * Fetched once, from `App.svelte`, the same "fire-and-forget on load" shape
 * `refreshAccount`/`refreshDaily` have: once per visit, cheap. The Play tab,
 * the You tab, the results screen and My scores all read this same shared
 * state rather than fetching their own copy: it is small (one row per mode
 * key, one per variant) and they all mount after `App.svelte`'s probe has
 * fired, so a second fetch would buy nothing but a race against the first.
 */
import { fetchRecords } from './records-io.js';
import type { PublicRecords, SoloRecordEntry, VersusLeaderEntry } from './records-io.js';

export type RecordsStatus =
  /** The probe has not answered yet. */
  | 'unknown'
  | 'ready'
  /** Offline, or the request otherwise failed. Never means "no database" — that answers `ready` with empty arrays. */
  | 'unavailable';

interface RecordsState {
  status: RecordsStatus;
  solo: readonly SoloRecordEntry[];
  versus: readonly VersusLeaderEntry[];
}

export const records: RecordsState = $state({
  status: 'unknown',
  solo: [],
  versus: [],
});

/**
 * Ask the server for the current records. Safe to call at any time; safe to
 * fail.
 *
 * Skipped entirely while the browser already says it is offline, the same
 * reason `account.svelte.ts#refresh` and `daily.svelte.ts#refreshDaily` both
 * skip their own probe: a failed request on every cold start of an installed
 * PWA is noise somebody will eventually chase, and `navigator.onLine ===
 * false` answers the question for free.
 */
export async function refreshRecords(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    records.status = 'unavailable';
    return;
  }
  const result: Awaited<ReturnType<typeof fetchRecords>> = await fetchRecords();
  if (result.status === 'ok') {
    applyRecords(result.value);
    return;
  }
  // `signed-out` cannot happen here — the route never refuses a guest — and
  // an `unavailable` (no database) answer from the server is itself mapped to
  // `ok` with empty arrays by the route, never to this branch. Every outcome
  // that does land here — offline, a genuine network failure — means the same
  // thing to this store: it could not ask.
  records.solo = [];
  records.versus = [];
  records.status = 'unavailable';
}

function applyRecords(value: PublicRecords): void {
  records.status = 'ready';
  records.solo = value.solo;
  records.versus = value.versus;
}

/** The global record for one `encodeModeConfig` key, or `null` when nobody holds one (or the table has not loaded yet). */
export function recordFor(modeKey: string): SoloRecordEntry | null {
  return records.solo.find((row) => row.modeKey === modeKey) ?? null;
}

/** The leader for one `RoomVariant`, or `null` when nobody has won one yet (or the table has not loaded yet). */
export function leaderFor(variant: string): VersusLeaderEntry | null {
  return records.versus.find((row) => row.variant === variant) ?? null;
}
